import {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo
} from 'react';
import {
  Table,
  Container,
  Button,
  SpaceBetween,
  Header,
  Box,
  Spinner,
  ProgressBar,
  Icon,
  Modal,
  Select,
  ButtonDropdown,
  Multiselect,
  TextFilter,
  Input,
  FormField,
  DateInput,
  Toggle
} from '@cloudscape-design/components';
import LoadingBar from '@cloudscape-design/chat-components/loading-bar';

// models
import {
  DOCTYPE,
  documentTableItem,
  KBOptions
} from '../lib/models/interfaces';
import { useCollection } from '@cloudscape-design/collection-hooks';
// helpers
// dummy data
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DocumentContext } from '../context/documentContext';
import { UserContext } from '../context/authContext';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { MoveDocumentToProject } from './Components/projectMoveForm';
import { ProjectContext } from '../context/projectContext';
// import { Share } from './Components/modelContents/share';
import { manageShare } from '../helpers/manageShareHelpers';
import { AppContext } from '../context/appContext';
import { formatIso_ddmmyyyy_hhmm } from '../helpers/dateHelpers';
import { createLogger } from '../helpers/Logger';
import { FileSelector } from './Components/uploadViews/FileSelector';
import { useAlert } from '../context/alertContext';
import { ScrollDownArrow } from './Components/shared/scrollDownArrow';
import { format } from 'date-and-time';
import { Loader, Save, SquarePen } from 'lucide-react';
import ButtonCollection, {
  ButtonConfig
} from './Components/shared/buttonCollection';

const logger = createLogger({ prefix: '[Document]' });

export const DocumentTableView = () => {
  const navigate = useNavigate();
  const {
    addDocToKnowledgeBase,
    allDocuments,
    setAllDocuments,
    getAllDocs,
    getAllDocumentsLoading
  } = useContext(DocumentContext);
  const {
    selectedProjects,
    selectedFolders,
    currentProjectFolders,
    mainProjects
  } = useContext(ProjectContext);
  const { auth, token, users, user } = useContext(UserContext);
  const { setNavOpen } = useContext(AppContext);
  const { setAlert } = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  // state
  const [selectedItemsLocal, setSelectedItemsLocal] = useState<
    documentTableItem[]
  >([]);
  // const [selectedItems, setSelectedItems] = useState<documentTableItem[]>([]);
  // const [allDocuments, setAllDocuments] = useState<documentTableItem[]>([]);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showDocTypeModal, setShowDocTypeModal] = useState<boolean>(false);
  const [docTypeValue, setDocTypeValue] = useState<OptionDefinition>({
    label: 'No Selection',
    value: 'NS'
  });
  const [kbLoading, setKBLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { deleteDocument } = useContext(DocumentContext);
  const [deleteCheckModla, setDeleteCheckModal] = useState<boolean>(false);
  const [addToKBModal, setAddToKBModal] = useState<boolean>(false);
  const [docsLoading, setDocLoading] = useState<boolean>(false);
  const [currentStorage, setCurrentStorage] = useState<number>(0);
  const [filteredByProject, setFilteredByProject] = useState<
    documentTableItem[]
  >([]);
  const [moveModal, setMoveModal] = useState<boolean>(false);
  const [folderList, setFolderList] = useState<OptionDefinition>({
    label: '',
    value: ''
  });
  const [moveLoading, setMoveLoading] = useState<boolean>(false);
  const [shareModalVisible, setShareModalVisible] = useState<boolean>(false);
  const [selectedShare, setSelectedShare] = useState<OptionDefinition[]>([]);
  const [shareDocLoading, setShareDocLoading] = useState<boolean>(false);
  const [deleteBulkModal, setDeleteBulkModal] = useState<boolean>(false);
  const [showReportButton, setShowReportButton] = useState<boolean>(false);
  const [currentTokens, setCurrentTokens] = useState<number>(0);
  const [showFileSelector, setShowFileSelector] = useState<boolean>(false);
  const [documentMetaData, setDocumentMetaData] = useState<{
    version: string | null;
    effectiveDate: string | null;
  }>({ version: null, effectiveDate: null });
  const [editingID, setEditingID] = useState<string | null>(null);
  const [documentMetaDataLoading, setDocumentMetaDataLoading] =
    useState<boolean>(false);

  //  case type === "documentMetaData":
  //     schema = Joi.object({
  //       documentId: Joi.string().required(),
  //       documentMetaData: Joi.object(),
  //     });

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectedFolders.length === 0) {
      setFilteredByProject([]);
    } else {
      const folderIds = selectedFolders.map((ele) => ele._id);
      logger.info('folder ids', folderIds);
      const filtered = allDocuments.filter((ele) =>
        folderIds.includes(ele?.folder)
      );
      setFilteredByProject(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDocuments, selectedFolders, selectedProjects, currentProjectFolders]);

  const getStorageSize = async () => {
    if (!auth.headers.Authorization) return;
    try {
      const storage = await axios.post(
        `${process.env.REACT_APP_KORU_API}/document?type=getGBSize`,
        {},
        auth
      );
      const convertToGB =
        (storage.data.result.totalSize / 1073741824 / 5) * 100;
      logger.info('converted size', convertToGB);
      setCurrentStorage(convertToGB);
      const tokens = await axios.post(
        `${process.env.REACT_APP_KORU_API}/document?type=tokensUsed`,
        {},
        auth
      );
      logger.info('token ', tokens);
    } catch (e) {
      const error = e as Error;
      logger.error('error in get storage', error.message);
    }
  };

  useEffect(() => {
    if (token) {
      getStorageSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDelete = async () => {
    const tempSelectedItems = selectedItemsLocal;
    if (selectedItemsLocal.length < 1) return;
    setDeleting(true);
    for (let i = 0; i < tempSelectedItems.length; i++) {
      const result = await deleteDocument(tempSelectedItems[i]);
      if (result?._id) {
        const tempAllDocs = allDocuments;
        const restDocs = tempAllDocs.filter(
          (ele) => ele._id !== tempSelectedItems[i]._id
        );
        setAllDocuments(restDocs);
      }
    }
    setSelectedItemsLocal([]);
    setDeleting(false);
    setDeleteCheckModal(false);
  };

  const handleBulkDelete = async () => {
    if (!token) return;
    const tempSelectedItems = selectedItemsLocal;
    if (selectedItemsLocal.length < 1) return;
    setDeleting(true);
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/document?type=bulkDeleteDocs`,
        { documents: tempSelectedItems },
        auth
      );
      if (result.status === 200) {
        setAlert('Bulk Delete Started', 'success', 3);
        setDeleting(false);
        setDeleteBulkModal(false);
        // remove the docs from local register;
        const deletedIds = tempSelectedItems.map((ele) => ele._id);
        const tempDocs = allDocuments.filter(
          (ele) => !deletedIds.includes(ele._id)
        );
        setAllDocuments(tempDocs);
      } else {
        throw Error('Error deleting');
      }
    } catch (e) {
      logger.error(e);
      setAlert('There was an error', 'error', 3);
    }
  };

  const handleAddToKB = async () => {
    setKBLoading(true);
    const result = await addDocToKnowledgeBase(
      selectedItemsLocal[0]._id,
      docTypeValue.value!
    );
    if (result.status === 200) {
      setDocTypeValue({ label: 'No Selection', value: 'NS' });
      setAddToKBModal(false);
      setSelectedItemsLocal([]);
      getAllDocs();
    } else {
      setErrorMessage('Error adding to KB');
    }
  };

  const handleNavigateUpload = (existing?: boolean) => {
    switch (true) {
      case selectedProjects[0].projectType === 'workspace':
        navigate(
          `/app/upload?projectId=${selectedProjects[0]._id}&folder=${selectedFolders[0]._id}`
        );
        break;
      case selectedProjects[0].projectType === 'information_request' &&
        !existing:
        navigate(
          `/app/upload?projectId=${selectedProjects[0]._id}&type=questionnaire&folder=${selectedFolders[0]._id}`
        );
        break;
      case selectedProjects[0].projectType === 'information_request' &&
        existing === true:
        navigate(
          `/app/upload?projectId=${selectedProjects[0]._id}&type=questionnaire&folder=${selectedFolders[0]._id}&prevFile=true`
        );
        break;
    }
  };

  const handleMoveProject = async () => {
    try {
      setMoveLoading(true);
      const documentIds = selectedItemsLocal.map((doc) => doc._id);
      const response = await axios.post(
        `${process.env.REACT_APP_KORU_API}/document?type=moveProject`,
        {
          documentIds,
          newFolder: folderList.value
        },
        auth
      );
      getAllDocs();
      setMoveLoading(false);
      setMoveModal(false);
    } catch (e) {
      const error = e as Error;
      logger.error('update error ', error.message);
    }
  };
  const handleDownloadDocument = async () => {
    try {
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getDocumentRFP`,
        {
          documentId: selectedItemsLocal[0]._id
        },
        auth
      );
      const getResult = await axios.get(presignedUrl.data.url, {
        responseType: 'blob'
      });

      const blob = new Blob([getResult.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedItemsLocal[0].documentFileName);
      document.body.appendChild(link);
      link.click();

      // 4. Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      logger.info('result from get xlsx', getResult);
    } catch (e) {
      const error = e as Error;
      logger.error('update error ', error.message);
    }
  };

  // const handleActionButton = (e: any) => {
  //   logger.info('e in action button', e.detail.id);
  //   switch (true) {
  //     case e.detail.id === 'moveFolder':
  //       setMoveModal(true);
  //       break;
  //     case e.detail.id === 'deleteSelected' &&
  //       selectedItems &&
  //       selectedItems.length === 1:
  //       setDeleteCheckModal(true);
  //       break;
  //     case e.detail.id === 'deleteSelected' &&
  //       selectedItems &&
  //       selectedItems.length > 1:
  //       setDeleteBulkModal(true);
  //       break;
  //     case e.detail.id === 'addKB':
  //       setAddToKBModal(true);
  //       break;
  //     case e.detail.id === 'reports':
  //       navigate(`/app/reports?documentId=${selectedItemsLocal[0]._id}`);
  //       break;
  //     case e.detail.id === 'addDocument':
  //       handleNavigateUpload();
  //       break;
  //     case e.detail.id === 'downloadDocument':
  //       handleDownloadDocument();
  //       break;
  //     case e.detail.id === 'addExisting':
  //       handleNavigateUpload(true);
  //       break;
  //   }
  // };

  const updateShare = async () => {
    if (!selectedItems) return;
    try {
      setShareDocLoading(true);
      logger.info('original', selectedItems[0].permissions);
      logger.info('new', selectedShare);
      const getChanges = manageShare(
        selectedItems[0].permissions.map((ele) => {
          return { value: ele.sub, label: ele.name };
        }),
        selectedShare
      );
      const reqBody: any = {
        _id: selectedItems[0]._id,
        remove: getChanges.removedItems.map((ele) => {
          return {
            sub: ele.value,
            action: 'view',
            name: ele.label
          };
        }),
        add: getChanges.addedItems.map((ele) => {
          return {
            sub: ele.value,
            action: 'view',
            name: ele.label
          };
        })
      };
      if (selectedItemsLocal[0].inheritPermissions === false) {
        reqBody.newInheritance = false;
      }
      const response = await axios.post(
        `${process.env.REACT_APP_KORU_API}/share?type=document`,
        reqBody,
        auth
      );
      logger.info('reqBody', reqBody);
      // update the specific documents permissions
      const updatedItem = response.data.result;
      const tempDocs = allDocuments;
      const specificDocIndex = tempDocs.findIndex(
        (ele) => ele._id === updatedItem._id
      );
      if (specificDocIndex !== -1) {
        tempDocs[specificDocIndex].permissions = updatedItem.permissions;
      }
      setAllDocuments([...tempDocs]);

      // logger.info('result', response.data);
      setShareDocLoading(false);
      setShareModalVisible(false);
      setSelectedShare([]);
      setSelectedItemsLocal([]);
    } catch (e) {
      const error = e as Error;
      logger.error('Update error ', error.message);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setAlert('You are not authorized to perform this action', 'error', 3);
      }
      setShareDocLoading(false);
      setShareDocLoading(false);
      setShareModalVisible(false);
      setSelectedShare([]);
      setSelectedItemsLocal([]);
    }
  };
  const handleShareModalCancel = () => {
    setShareDocLoading(false);
    setShareModalVisible(false);
    setSelectedShare([]);
  };

  const showReportOption = () => {
    let valueToSet = false;
    if (selectedItemsLocal.length === 0 || mainProjects.length === 0)
      return false;
    if (selectedItems && selectedItemsLocal.length === 1) {
      const currentProjectType = mainProjects.filter(
        (ele) => ele._id === selectedItemsLocal[0].project!
      );

      if (currentProjectType[0]?.projectType === 'workspace') {
        valueToSet = true;
      }
    }
    setShowReportButton(valueToSet);
  };
  // useEffect(() => {
  //   showReportOption();
  // }, [selectedItemsLocal, mainProjects]);

  const emptyState = useCallback(() => {
    if (docsLoading && allDocuments.length === 0) {
      return <Spinner></Spinner>;
    } else {
      return (
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <b>No Documents yet</b>
            <Button
              onClick={() => setShowFileSelector(true)}
              disabled={selectedFolders.length !== 1 ? true : false}
            >
              Upload document
            </Button>
          </SpaceBetween>
        </Box>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDocuments, docsLoading, selectedFolders]);

  const { items, collectionProps, filterProps, actions } = useCollection(
    filteredByProject,
    {
      filtering: {
        empty: <Box>Nothing</Box>,
        noMatch: <Box>No matches</Box>,
        filteringFunction: (item, filteringText) => {
          const normalizedFilterText = (
            filteringText ?? ''
          ).toLocaleLowerCase();
          return item.documentFileName
            .toLowerCase()
            .includes(normalizedFilterText);
        }
      },
      // pagination: { pageSize: 8 },
      sorting: {},
      selection: {}
    }
  );

  useEffect(() => {
    const fileName = searchParams.get('fileName');
    if (fileName) {
      actions.setFiltering(fileName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectedDocPermissions = () => {
    const permissionsList = selectedItems![0]!.permissions;
    const tempOptionsList = permissionsList.map((member) => {
      return {
        label: member.name,
        value: member.sub
      };
    });
    setSelectedShare([...tempOptionsList]);
  };
  const { selectedItems } = collectionProps;

  useEffect(() => {
    const tempSelect = JSON.stringify(selectedItems);
    const parsedTempSelect = JSON.parse(tempSelect);
    setSelectedItemsLocal(parsedTempSelect);
    if (selectedItems && selectedItems.length === 1) {
      selectedDocPermissions();
      setDocumentMetaData(selectedItems[0].documentMetaData);
    }
    if (selectedItems && selectedItems.length !== 1) {
      setEditingID(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, allDocuments]);

  const viewButtonDisabled = () => {
    if (selectedItemsLocal.length === 0) {
      return true;
    }
    if (
      selectedItemsLocal[0]?.documentType === 'analysis' &&
      selectedItemsLocal.length === 0
    ) {
      return true;
    }
    if (
      selectedItemsLocal[0]?.documentType === 'rfp' &&
      selectedItemsLocal.length > 1
    ) {
      return true;
    }
    if (
      selectedItemsLocal[0].documentType === 'GapAnalysis' &&
      selectedItemsLocal.length > 1
    ) {
      return true;
    }
    if (
      selectedItemsLocal[0].documentType === 'GapAnalysis' &&
      selectedItemsLocal[0].status !== 'Verification Needed'
    ) {
      return true;
    }
    return false;
  };

  const handleSaveMetaData = async (id: string) => {
    if (!token || !id) return;
    try {
      setDocumentMetaDataLoading(true);
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/document?type=documentMetaData`,
        {
          documentId: id,
          documentMetaData: {
            version: documentMetaData.version,
            effectiveDate: documentMetaData.effectiveDate
          }
        },
        auth
      );
      console.log('resu;t from update', result.data.result);
      setAlert('Information Updated', 'success', 3);
      setDocumentMetaDataLoading(false);
      getAllDocs();
      setDocumentMetaData({ version: null, effectiveDate: null });
      setEditingID('');
    } catch (e) {
      setAlert('Information Not Updated', 'error', 3);
      setDocumentMetaDataLoading(false);
    }
  };

  const columnDefs = () => {
    if (selectedProjects[0]?.projectType === 'workspace') {
      return [
        {
          id: 'title',
          header: 'Document',
          cell: (item: documentTableItem) => item.documentFileName,
          sortingField: 'documentFileName',
          maxWidth: '400px'
        },
        {
          id: 'docStatus',
          header: 'Status',
          maxWidth: '200px',
          cell: (item: documentTableItem) => {
            console.log('item status', item.status);
            if (item.status === 'analyzed') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Analysis Complete</Box>
                  <ProgressBar status="success" />
                </SpaceBetween>
              );
            }
            if (item.status === 'analyzed - stage 2') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Analysis Complete</Box>
                  <ProgressBar status="success" />
                </SpaceBetween>
              );
            }
            if (item.status === 'analyzed - stage 1') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Initial Analysis Complete</Box>
                  <ProgressBar status="success" />
                </SpaceBetween>
              );
            }
            if (item.status === 'Analyzing') {
              return <LoadingBar variant="gen-ai" />;
            }
            if (item.status === 'Processing Error') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Processing Error</Box>
                  <Icon name="status-negative"></Icon>
                </SpaceBetween>
              );
            }
            if (item.status === 'Published') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Published</Box>
                  <ProgressBar status="success" />
                </SpaceBetween>
              );
            }
            if (item.status.toLowerCase() === 'needs confirmation') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Please Verify Input</Box>
                </SpaceBetween>
              );
            }
            if (
              (item.status === 'Needs Verification' ||
                item.status === 'Verification Needed') &&
              !item?.finalized
            ) {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Verify Answers</Box>
                </SpaceBetween>
              );
            }
            if (item.status === 'building answers') {
              return <LoadingBar variant="gen-ai" />;
            }
            if (item?.finalized) {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Finalized</Box>
                </SpaceBetween>
              );
            }
          },
          sortingField: 'status'
        },
        {
          id: 'createDate',
          header: `Uploaded`,
          cell: (item: documentTableItem) =>
            formatIso_ddmmyyyy_hhmm(item.createdAt),
          sortingField: `createdAt`
        },
        {
          id: 'permissions',
          header: 'Access',
          cell: (item: documentTableItem) => {
            if (item.searchAble) {
              const allPermissions = [...item.permissions, { name: 'KB' }];
              return `${allPermissions.map((ele) => ele.name).join(' , ')}`;
            } else {
              return `${item.permissions.map((ele) => ele.name).join(' , ')}`;
            }
          },
          sortingField: 'permissions'
        },
        {
          id: 'pii',
          header: 'Sensitive Data Detected',
          cell: (item: documentTableItem) =>
            `${item?.entitiesDetected && item.entitiesDetected.length > 0 ? 'PII' : ''}`,
          sortingField: 'entitiesDetected'
        },
        {
          id: 'version',
          header: 'Version',
          cell: (item: documentTableItem) => {
            if (
              selectedItems &&
              selectedItems.length === 1 &&
              selectedItems[0]._id === item._id &&
              editingID === item._id
            ) {
              return (
                <Input
                  type="text"
                  value={`${documentMetaData.version ? documentMetaData.version : ''}`}
                  onChange={({ detail }) =>
                    setDocumentMetaData((prev) => ({
                      ...prev,
                      version: detail.value
                    }))
                  }
                />
              );
            } else {
              return `${item.documentMetaData.version ? item.documentMetaData.version : ''}`;
            }
          },
          sortingField: 'documentMetaData.version'
        },
        {
          id: 'effectiveDate',
          header: 'Effective Date',
          cell: (item: documentTableItem) => {
            if (
              selectedItems &&
              selectedItems.length === 1 &&
              selectedItems[0]._id === item._id &&
              editingID === item._id
            ) {
              return (
                <FormField
                  label="Effective Date"
                  constraintText="Use YYYY/MM/DD format."
                >
                  <DateInput
                    onChange={({ detail }) =>
                      setDocumentMetaData((prev) => ({
                        ...prev,
                        effectiveDate: detail.value
                      }))
                    }
                    value={documentMetaData.effectiveDate!}
                    placeholder="YYYY/MM/DD"
                  />
                </FormField>
              );
            } else {
              return `${item.documentMetaData.effectiveDate ? format(new Date(item.documentMetaData.effectiveDate.split('T')[0]), 'MM-DD-YYYY') : ''}`;
            }
          },
          sortingField: 'documentMetaData.effectiveDate',
          sortingComparator: (
            a: {
              documentMetaData: { effectiveDate: string | null | undefined };
            },
            b: {
              documentMetaData: { effectiveDate: string | null | undefined };
            }
          ) => {
            const toTime = (val: string | null | undefined) => {
              if (!val) return -Infinity;
              const time = Date.parse(val);
              return Number.isFinite(time) ? time : -Infinity;
            };
            const aTime = toTime(a.documentMetaData?.effectiveDate);
            const bTime = toTime(b.documentMetaData?.effectiveDate);
            return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
          }
        },
        {
          id: 'saveButton',
          header: '',
          cell: (item: documentTableItem) => {
            if (
              selectedItems &&
              selectedItems.length === 1 &&
              selectedItems[0]._id === item._id &&
              (selectedItems[0].documentMetaData.version !==
                documentMetaData.version ||
                selectedItems[0].documentMetaData.effectiveDate !==
                  documentMetaData.effectiveDate)
            ) {
              return (
                <button onClick={() => handleSaveMetaData(item._id)}>
                  {documentMetaDataLoading ? (
                    <Loader color="#56dac5" />
                  ) : (
                    <Save color="#56dac5" />
                  )}
                </button>
              );
            }
            if (
              selectedItems &&
              selectedItems.length === 1 &&
              selectedItems[0]._id === item._id
            ) {
              return (
                <button onClick={() => setEditingID(item._id)}>
                  <SquarePen color="#56dac5" />
                </button>
              );
            }
          }
        }
      ];
    } else {
      // Below is information_request folders
      return [
        {
          id: 'title',
          header: 'Document',
          cell: (item: documentTableItem) => item.documentFileName,
          sortingField: 'documentFileName',
          maxWidth: '400px'
        },
        {
          id: 'docStatus',
          header: 'Status',
          maxWidth: '200px',
          cell: (item: documentTableItem) => {
            if (item.status === 'analyzed') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Analysis Complete</Box>
                  <ProgressBar status="success" />
                </SpaceBetween>
              );
            }
            if (item.status === 'Analyzing') {
              return <LoadingBar variant="gen-ai" />;
            }
            if (item.status === 'Processing Error') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Processing Error</Box>
                  <Icon name="status-negative"></Icon>
                </SpaceBetween>
              );
            }
            if (item.status === 'Published') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Published</Box>
                  <ProgressBar status="success" />
                </SpaceBetween>
              );
            }
            if (item.status === 'running gap analysis') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Performing GAP Analysis</Box>
                </SpaceBetween>
              );
            }
            if (item.status.toLowerCase() === 'needs confirmation') {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Please Verify Input</Box>
                </SpaceBetween>
              );
            }
            if (
              (item.status === 'Needs Verification' ||
                item.status === 'Verification Needed') &&
              !item?.finalized
            ) {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Verify Answers</Box>
                </SpaceBetween>
              );
            }
            if (item.status === 'building answers') {
              return <LoadingBar variant="gen-ai" />;
            }
            if (item?.finalized) {
              return (
                <SpaceBetween size="l" direction="horizontal">
                  <Box>Finalized</Box>
                </SpaceBetween>
              );
            }
          },
          sortingField: 'status'
        },
        {
          id: 'company_for',
          header: 'Company',
          cell: (item: documentTableItem) => item?.company_for ?? '',
          sortingField: 'company_for',
          maxWidth: '200px'
        },
        {
          id: 'company_contact_person',
          header: 'Contact',
          cell: (item: documentTableItem) => item?.company_contact_person ?? '',
          sortingField: 'company_contact_person',
          maxWidth: '200px'
        },
        {
          id: 'createDate',
          header: `Uploaded`,
          cell: (item: documentTableItem) =>
            formatIso_ddmmyyyy_hhmm(item.createdAt)
        },
        {
          id: 'permissions',
          header: 'Access',
          cell: (item: documentTableItem) =>
            `${item.permissions.map((ele) => ele.name).join(' , ')}`
        },
        {
          id: 'version',
          header: 'Version',
          cell: (item: documentTableItem) =>
            `${item.documentMetaData.version ? item.documentMetaData.version : ''}`,
          sortingField: 'documentMetaData.version'
        },
        {
          id: 'effectiveDate',
          header: 'Effective Date',
          cell: (item: documentTableItem) =>
            `${item.documentMetaData.effectiveDate ? item.documentMetaData.effectiveDate : ''}`,
          sortingField: 'documentMetaData.effectiveDate',
          sortingComparator: (
            a: {
              documentMetaData: { effectiveDate: string | null | undefined };
            },
            b: {
              documentMetaData: { effectiveDate: string | null | undefined };
            }
          ) => {
            const toTime = (val: string | null | undefined) => {
              if (!val) return -Infinity;
              const time = Date.parse(val);
              return Number.isFinite(time) ? time : -Infinity;
            };
            const aTime = toTime(a.documentMetaData?.effectiveDate);
            const bTime = toTime(b.documentMetaData?.effectiveDate);
            return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
          }
        }
      ];
    }
  };

  // buttonFunctions
  const buttonViewDocs = () => {
    if (selectedItemsLocal[0]?.documentType === 'analysis') {
      navigate(
        `/app/simple-viewer?${selectedItems?.map((ele) => `documentId=${ele._id}`).join('&')}&projectId=${selectedProjects[0]._id}`
      );
    } else if (
      selectedItemsLocal[0].documentType === 'rfp' ||
      selectedItemsLocal[0].documentType === 'prev-rfp' ||
      selectedItemsLocal[0].documentType === 'GapAnalysis'
    ) {
      navigate(
        `/app/rfp-upload?documentId=${selectedItemsLocal[0]._id}&projectId=${selectedProjects[0]._id}`
      );
    } else {
      navigate(
        `/app/questionnaire?documentId=${selectedItemsLocal[0]._id}&projectId=${selectedProjects[0]._id}`
      );
    }
    setNavOpen(false);
  };

  const buttonsInfo: ButtonConfig[] = useMemo(
    () => [
      {
        label: `${selectedItems && selectedItems.length > 1 ? 'View Documents' : 'View Document'}`,
        buttonFunctions: buttonViewDocs,
        type: 'main',
        variant: 'primary',
        disabled: viewButtonDisabled()
      },
      {
        label: 'Upload Files',
        buttonFunctions: () => setShowFileSelector(true),
        type: 'main',
        variant: 'primary',
        disabled:
          !(selectedFolders.length === 1 && selectedProjects.length === 1) ||
          !selectedFolders[0]?.permissions?.some(
            (permission) => permission.sub === user.sub
          )
      },

      {
        label: 'Delete',
        buttonFunctions: () => {
          if (selectedItemsLocal.length === 1) {
            setDeleteCheckModal(true);
          }
          if (selectedItemsLocal.length > 1) {
            setDeleteBulkModal(true);
          }
          return;
        },
        type: 'additional',
        variant: 'normal',
        disabled: selectedItemsLocal.length === 0
      },
      {
        label: 'Share',
        buttonFunctions: () => setShareModalVisible(true),
        type: 'additional',
        variant: 'normal',
        disabled: selectedItemsLocal.length !== 1,
        wrapperText: 'Update Document Permissions'
      },
      {
        label: 'Move',
        buttonFunctions: () => setMoveModal(true),
        type: 'additional',
        variant: 'normal',
        disabled:
          selectedFolders.length !== 1 || !(selectedItemsLocal.length > 0),
        wrapperText: 'Move Document to a different folder'
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemsLocal, selectedItems, selectedFolders, selectedProjects]
  );
  // add it specifics based on workspace type;
  useMemo(() => {
    if (selectedProjects[0]?.projectType === 'workspace') {
      // check if the button is already in the array.
      const index = buttonsInfo.findIndex(
        (button) => button.label === 'Add To Knowledgebase'
      );
      if (index === -1) {
        buttonsInfo.push({
          label: 'Add To Knowledgebase',
          buttonFunctions: () => setAddToKBModal(true),
          type: 'additional',
          disabled: selectedItemsLocal.length !== 1,
          wrapperText: 'Make this document searchable'
        });
      }
    } else {
      const indexD = buttonsInfo.findIndex(
        (button) => button.label === 'Download'
      );
      if (indexD === -1) {
        buttonsInfo.push({
          label: 'Download',
          buttonFunctions: () => handleDownloadDocument(),
          type: 'additional',
          disabled: selectedItemsLocal.length !== 1,
          wrapperText: 'Finalized Documents can be downloaded'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjects, selectedItemsLocal, filteredByProject]);

  return (
    <>
      <Container
        header={
          <Header
            actions={<ButtonCollection buttons={buttonsInfo} />}
            description={
              selectedProjects &&
              selectedProjects.length > 0 &&
              selectedProjects[0]?.projectType! === 'workspace'
                ? 'Select Documents to View or analyze.'
                : 'Select a Document to view.'
            }
          >
            Documents in Folder
          </Header>
        }
      >
        <div
          style={{
            maxHeight: '40vh',
            overflowY: 'auto',
            scrollbarWidth: 'none'
          }}
          ref={containerRef}
        >
          <Table
            stickyHeader
            stripedRows
            variant="borderless"
            {...collectionProps}
            contentDensity="compact"
            items={items}
            selectionType="multi"
            columnDefinitions={columnDefs()}
            loadingText="Loading resources"
            empty={emptyState()}
            selectedItems={selectedItems}
            filter={
              <TextFilter
                {...filterProps}
                filteringPlaceholder="Search Documents"
                onChange={(event) => {
                  // 1) Call Cloudscape/useCollection handler so filtering still works
                  filterProps.onChange?.(event);

                  // 2) Your custom logic
                  const value = event.detail.filteringText;
                  if (value === '') {
                    searchParams.delete('fileName');
                    setSearchParams(searchParams);
                  }
                }}
              />
            }
            // onSelectionChange={({ detail }) =>
            //   setSelectedItems(detail.selectedItems)
            // }
          />
          <ScrollDownArrow
            containerRef={containerRef as React.RefObject<HTMLElement>}
            bottomValue={'7%'}
          />
        </div>
      </Container>
      <Modal
        visible={showDocTypeModal}
        header={<Header>Select Document Type</Header>}
        size="large"
        onDismiss={() => {
          setShowDocTypeModal(false);
        }}
      ></Modal>
      <Modal
        visible={deleteCheckModla}
        header={<Header>Delete Selected Document</Header>}
        onDismiss={() => setDeleteCheckModal(false)}
        footer={
          <SpaceBetween size={'s'} direction="horizontal">
            <Button onClick={() => setDeleteCheckModal(false)}>Cancel</Button>
            <Button
              disabled={selectedItemsLocal[0]?.searchAble && !user.isAdmin}
              onClick={() => handleDelete()}
              loading={deleting}
            >
              Delete
            </Button>
          </SpaceBetween>
        }
      >
        {selectedItemsLocal[0]?.searchAble &&
          !user.isAdmin &&
          'The selected Document is part of the Knowledge Base and Only be deleted by an Admin user'}
      </Modal>
      <Modal
        visible={deleteBulkModal}
        header={<Header>Delete Selected Documents</Header>}
        onDismiss={() => setDeleteCheckModal(false)}
        footer={
          <SpaceBetween size={'s'} direction="horizontal">
            <Button onClick={() => setDeleteBulkModal(false)}>Cancel</Button>
            <Button
              disabled={
                selectedItems?.some((ele) => ele.searchAble) && !user.isAdmin
              }
              onClick={() => handleBulkDelete()}
              loading={deleting}
            >
              Delete
            </Button>
          </SpaceBetween>
        }
      >
        {selectedItems?.some((ele) => ele.searchAble) && !user.isAdmin && (
          <div>
            The Underlined Documents are part of the Knowledge Base and cannot
            be deleted.
          </div>
        )}
        {selectedItems?.some((ele) => ele.searchAble) && user.isAdmin && (
          <div className="p-4">
            The Underlined Documents are part of the Knowledge Base and can only
            be deleted by admin users. Please make sure that you want to delete
            the Knowledge Base Documents.
          </div>
        )}
        <SpaceBetween size={'s'} direction="vertical">
          {selectedItems &&
            selectedItems?.length > 0 &&
            selectedItems?.map((ele) => (
              <p
                className={`${ele.searchAble ? 'underline decoration-red-500' : ''}`}
              >
                {ele.documentFileName}
              </p>
            ))}
        </SpaceBetween>
      </Modal>
      <Modal
        visible={addToKBModal}
        header={<Header>Make the Document Searchable</Header>}
        onDismiss={() => setAddToKBModal(false)}
        footer={
          <SpaceBetween size={'s'} direction="horizontal">
            <Button onClick={() => setAddToKBModal(false)}>Cancel</Button>
            <Button onClick={() => handleAddToKB()} loading={deleting}>
              Add Document
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size={'s'} direction="vertical">
          <p>
            CAUTION: This will make the document searchable as well as viewable
            by anyone in your organization.
          </p>
          <Select
            selectedOption={docTypeValue}
            options={KBOptions}
            onChange={({ detail }) => setDocTypeValue(detail.selectedOption)}
            expandToViewport
          ></Select>
        </SpaceBetween>
      </Modal>
      <Modal
        visible={moveModal}
        header={<Header>Move Document</Header>}
        onDismiss={() => setMoveModal(false)}
        footer={
          <SpaceBetween size={'s'} direction="horizontal">
            <Button onClick={() => setMoveModal(false)}>Cancel</Button>
            <Button onClick={() => handleMoveProject()} loading={moveLoading}>
              Move Document
            </Button>
          </SpaceBetween>
        }
      >
        <MoveDocumentToProject
          folderList={folderList}
          setFolderList={setFolderList}
          filteredFolders={currentProjectFolders}
        />
      </Modal>
      <Modal
        size="large"
        visible={shareModalVisible}
        onDismiss={() => handleShareModalCancel()}
        header={'Document Access'}
        footer={
          <div className="w-full flex justify-between">
            {selectedItems && selectedItems.length > 0 && (
              <>
                <div className="space-x-2">
                  <Button
                    onClick={() => {
                      handleShareModalCancel();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => updateShare()}
                    loading={shareDocLoading}
                  >
                    Update Access
                  </Button>
                </div>
                <div>
                  <Toggle
                    disabled={!selectedItems[0]?.inheritPermissions}
                    checked={selectedItemsLocal[0]?.inheritPermissions}
                    onChange={({ detail }) =>
                      setSelectedItemsLocal((prev) =>
                        prev.map((item) => ({
                          ...item,
                          inheritPermissions: detail.checked
                        }))
                      )
                    }
                  >
                    Inherit Folder Access
                  </Toggle>
                </div>
              </>
            )}
          </div>
        }
      >
        <div
          style={{
            height: '40vh'
          }}
        >
          <p>
            Choose who can access this document. Access will apply only to this
            document.
          </p>
          <Multiselect
            onChange={({ detail }) =>
              setSelectedShare([...detail.selectedOptions])
            }
            placeholder="Users"
            selectedOptions={selectedShare}
            options={users.map((ele) => {
              return {
                label: ele.name,
                value: ele.sub
                // disabled: ele.sub === user.sub
              };
            })}
          ></Multiselect>
        </div>
      </Modal>
      <Modal
        visible={showFileSelector}
        onDismiss={() => setShowFileSelector(false)}
      >
        <div className="max-h-[50vh] overflow-y-scroll">
          {selectedProjects.length > 0 && selectedFolders.length === 1 && (
            <FileSelector
              type={
                selectedProjects[0]?.projectType === 'workspace'
                  ? DOCTYPE.analysis
                  : DOCTYPE.questionnaire
              }
              projectId={selectedProjects[0]?._id}
              folderId={selectedFolders[0]?._id}
              handleModalCancel={setShowFileSelector}
            />
          )}
        </div>
      </Modal>
    </>
  );
};
